import {
  HHButton,
  HHStack,
  HHTextField,
  HHTypography,
} from '@hinge-health/react-component-library';
import AddIcon from '@mui/icons-material/Add';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Bundle, Item } from '../../api/types';
import { ContractInput, FeeScheduleLineDecision } from '../context/types';
import { useWizardState } from '../context/wizard-state.context';
import { BundlePicker } from './bundle';
import { KGItemPicker } from './item';

const monoCell = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontVariantNumeric: 'tabular-nums' as const,
  fontSize: 13,
};

interface LineManagerProps {
  input: ContractInput;
}

const itemLabel = (i: Item): string =>
  i.technicalName ?? i.colloquialName ?? i.description ?? '(unnamed item)';

export const LineManager = ({ input }: LineManagerProps): JSX.Element => {
  const { state, setState } = useWizardState();
  const fs = state.decisions.feeSchedule;
  const lines = useMemo(() => fs?.lines ?? [], [fs]);

  const [addMode, setAddMode] = useState<'bundle' | 'item' | null>(null);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editingPrice, setEditingPrice] = useState('');
  const priceInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editingIdx !== null) {
      priceInputRef.current?.focus();
    }
  }, [editingIdx]);

  const existingRefIds = useMemo(
    () => new Set(lines.map(l => l.refId)),
    [lines],
  );

  const isFirstBundle = !lines.some(l => l.kind === 'bundle');
  const ratePrefill = input.fee_schedule.rate_per_case;

  const addLine = (line: FeeScheduleLineDecision): void => {
    setState(s => {
      const cur = s.decisions.feeSchedule;
      if (cur == null) return s;
      return {
        ...s,
        decisions: {
          ...s.decisions,
          feeSchedule: { ...cur, lines: [...cur.lines, line] },
        },
      };
    });
  };

  const removeLine = (idx: number): void => {
    setState(s => {
      const cur = s.decisions.feeSchedule;
      if (cur == null) return s;
      return {
        ...s,
        decisions: {
          ...s.decisions,
          feeSchedule: {
            ...cur,
            lines: cur.lines.filter((_, i) => i !== idx),
          },
        },
      };
    });
  };

  const commitPriceEdit = (idx: number): void => {
    const p = Number(editingPrice);
    if (!Number.isNaN(p) && p > 0) {
      setState(s => {
        const cur = s.decisions.feeSchedule;
        if (cur == null) return s;
        return {
          ...s,
          decisions: {
            ...s.decisions,
            feeSchedule: {
              ...cur,
              lines: cur.lines.map((l, i) =>
                i === idx ? { ...l, priceDollars: p } : l,
              ),
            },
          },
        };
      });
    }
    setEditingIdx(null);
  };

  const onSelectBundle = (bundle: Bundle): void => {
    if (bundle.id == null || existingRefIds.has(bundle.id)) return;
    const prefill = isFirstBundle && ratePrefill != null ? ratePrefill : 0;
    const newIdx = lines.length;
    addLine({
      kind: 'bundle',
      refId: bundle.id,
      priceDollars: prefill,
      label: bundle.name,
    });
    setAddMode(null);
    setEditingIdx(newIdx);
    setEditingPrice(prefill > 0 ? prefill.toFixed(2) : '');
  };

  const onSelectItem = (item: Item): void => {
    if (item.id == null || existingRefIds.has(item.id)) return;
    const newIdx = lines.length;
    addLine({
      kind: 'item',
      refId: item.id,
      priceDollars: 0,
      label: itemLabel(item),
    });
    setAddMode(null);
    setEditingIdx(newIdx);
    setEditingPrice('');
  };

  return (
    <HHStack spacing={2}>
      {lines.length === 0 ? (
        <HHTypography hhVariant="body2" sx={{ opacity: 0.6 }}>
          No service lines yet. Add a bundle or item below.
        </HHTypography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 80 }}>Kind</TableCell>
              <TableCell>Label</TableCell>
              <TableCell sx={{ width: 240 }}>Ref ID</TableCell>
              <TableCell sx={{ width: 150 }} align="right">
                Price (USD)
              </TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((l, idx) => (
              <TableRow key={`${l.kind}:${l.refId}`}>
                <TableCell sx={monoCell}>{l.kind}</TableCell>
                <TableCell>{l.label}</TableCell>
                <TableCell sx={monoCell}>{l.refId}</TableCell>
                <TableCell align="right">
                  {editingIdx === idx ? (
                    <HHTextField
                      hhVariant="variant-bypass"
                      size="small"
                      inputRef={priceInputRef}
                      value={editingPrice}
                      onChange={(e): void => setEditingPrice(e.target.value)}
                      onBlur={(): void => commitPriceEdit(idx)}
                      onKeyDown={(e): void => {
                        if (e.key === 'Enter') commitPriceEdit(idx);
                        if (e.key === 'Escape') setEditingIdx(null);
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                      inputProps={{
                        inputMode: 'decimal',
                        style: { textAlign: 'right', width: 80 },
                      }}
                    />
                  ) : l.priceDollars > 0 ? (
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={(): void => {
                        setEditingIdx(idx);
                        setEditingPrice(l.priceDollars.toFixed(2));
                      }}
                      onKeyDown={(e): void => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setEditingIdx(idx);
                          setEditingPrice(l.priceDollars.toFixed(2));
                        }
                      }}
                      title="Click to edit price"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        p: 1,
                        borderRadius: 1,
                        border: '1px solid rgba(0,0,0,0.15)',
                        backgroundColor: 'rgba(0,0,0,0.02)',
                        transition:
                          'background-color 0.15s, border-color 0.15s',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.05)',
                          borderColor: 'rgba(0,0,0,0.28)',
                        },
                        '& .edit-icon': { opacity: 0.35, fontSize: 14 },
                        '&:hover .edit-icon': { opacity: 0.65 },
                        ...monoCell,
                      }}
                    >
                      {`$${l.priceDollars.toFixed(2)}`}
                      <EditIcon className="edit-icon" />
                    </Box>
                  ) : (
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={(): void => {
                        setEditingIdx(idx);
                        setEditingPrice('');
                      }}
                      onKeyDown={(e): void => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setEditingIdx(idx);
                          setEditingPrice('');
                        }
                      }}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 0.5,
                        border: '1px dashed',
                        borderColor: 'warning.main',
                        color: 'warning.dark',
                        fontSize: 12,
                        fontWeight: 500,
                        '&:hover': {
                          backgroundColor: 'rgba(237, 108, 2, 0.06)',
                        },
                      }}
                    >
                      <AttachMoneyIcon sx={{ fontSize: 14 }} />
                      Set price
                    </Box>
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(): void => removeLine(idx)}
                    aria-label={`Remove ${l.label}`}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {addMode == null && (
        <HHStack direction="row" spacing={2}>
          <HHButton
            hhVariant="variant-bypass"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={(): void => setAddMode('bundle')}
          >
            Add bundle
          </HHButton>
          <HHButton
            hhVariant="variant-bypass"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={(): void => setAddMode('item')}
          >
            Add item
          </HHButton>
        </HHStack>
      )}

      {addMode === 'bundle' && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <HHStack spacing={2}>
            <HHStack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <HHTypography hhVariant="subtitle2">Pick a bundle</HHTypography>
              <HHButton
                hhVariant="variant-bypass"
                variant="text"
                size="small"
                onClick={(): void => setAddMode(null)}
              >
                Cancel
              </HHButton>
            </HHStack>
            <BundlePicker
              disabledIds={existingRefIds}
              onSelect={onSelectBundle}
            />
            {isFirstBundle && ratePrefill != null && (
              <HHTypography hhVariant="caption" sx={{ opacity: 0.65 }}>
                First bundle pre-filled at ${ratePrefill.toFixed(2)}/case from
                the contract input — adjust in the price field that opens.
              </HHTypography>
            )}
          </HHStack>
        </Paper>
      )}

      {addMode === 'item' && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <HHStack spacing={2}>
            <HHStack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <HHTypography hhVariant="subtitle2">Pick an item</HHTypography>
              <HHButton
                hhVariant="variant-bypass"
                variant="text"
                size="small"
                onClick={(): void => setAddMode(null)}
              >
                Cancel
              </HHButton>
            </HHStack>
            <KGItemPicker
              disabledIds={existingRefIds}
              onSelect={onSelectItem}
            />
            <HHTypography hhVariant="caption" sx={{ opacity: 0.65 }}>
              The price field will open automatically after selecting an item.
            </HHTypography>
          </HHStack>
        </Paper>
      )}
    </HHStack>
  );
};
